import { Component } from 'cc';
import Game from '../../Game';

type EventTargetLike = {
    once: (eventType: string | number, listener: (...any: any[]) => void, target?: any) => void;
    off?: (eventType: string | number, listener: (...any: any[]) => void, target?: any) => void;
};

namespace Delay {
    const component = new Component();
    const neverResolve = new Promise<never>(() => {});

    /**
     * 過了特定時間後，會 fullfill 的 promise
     * @param millisecond 指定時間
     */
    export function resolve(millisecond: number = 0, comp: Component = component): Promise<void> {
        return new Promise<void>((resolveFunc) => {
            comp.scheduleOnce(resolveFunc, millisecond / 1000);
        });
    }
    /**
     * 在指定時間 fullfill 的 promise，若超過指定時間則 reject
     * @param time 時間
     * @param isTimeStamp 為True時, 代表time為時間戳記, 否則為Delay多久(毫秒)
     * @param exceedTime setTimeout超出時間限制，預設為無限大
     */
    export function timeout(time: number, isTimeStamp: boolean = false, exceedTime: number = Number.POSITIVE_INFINITY): Promise<void> {
        return new Promise<void>((resolveFunc, rejectFunc) => {
            setTimeout(
                () => {
                    if (Date.now() - exceedTime > time) {
                        rejectFunc();
                    } else {
                        resolveFunc();
                    }
                },
                isTimeStamp ? time - Date.now() : time,
            );
        });
    }
    /**
     * 過了特定時間後，會 reject 並且回傳指定錯誤原因的 promise
     * @param millisecond 指定時間
     * @param reason 指定錯誤原因
     * @returns
     */
    export function reject<T>(millisecond: number, reason: any, useTimeout: boolean = false): Promise<T> {
        return new Promise<T>((_, rejectFunc) => {
            if (millisecond === Infinity || millisecond === -Infinity || millisecond === Number.MAX_SAFE_INTEGER || millisecond === Number.MIN_SAFE_INTEGER) {
                return;
            }
            if (useTimeout) {
                setTimeout(() => rejectFunc(reason), millisecond);
            } else {
                component.scheduleOnce(() => rejectFunc(reason), millisecond / 1000);
            }
        });
    }

    /**
     * 過了特定時間後，會 fullfill 並且回傳指定值的 promise
     * @param millisecond 指定時間
     * @param resolveValue 指定回傳值
     * @returns
     */
    export function value<T>(millisecond: number, resolveValue: T): Promise<T> {
        return new Promise<T>((resolveFunc) => component.scheduleOnce(() => resolveFunc(resolveValue), millisecond / 1000));
    }

    /**
     * 永遠不會 fullfill 的 promise
     */
    export function forever(): Promise<never> {
        return neverResolve;
    }

    /**
     * 等待目標事件觸發並通過條件判斷後會 fullfill 的 promise
     * @param eventType 目標事件
     * @param target 監聽物件，預設為 Game.event
     * @param isPass 判斷當前事件是否符合條件，預設永遠回傳 true
     */
    function _event<T extends string | number>(
        eventType: T,
        target?: EventTargetLike,
        isPass?: (...args: any) => boolean,
        cancellable?: false,
        withArgs?: false,
    ): Promise<T>;
    function _event<T extends string | number>(
        eventType: T,
        target?: EventTargetLike,
        isPass?: (...args: any) => boolean,
        cancellable?: false,
        withArgs?: true,
    ): Promise<[T, any]>;
    function _event<T extends string | number>(
        eventType: T,
        target?: EventTargetLike,
        isPass?: (...args: any) => boolean,
        cancellable?: true,
        withArgs?: false,
    ): [Promise<T>, () => void];
    function _event<T extends string | number>(
        eventType: T,
        target?: EventTargetLike,
        isPass?: (...args: any) => boolean,
        cancellable?: true,
        withArgs?: true,
    ): [Promise<[T, any]>, () => void];
    // eslint-disable-next-line no-inner-declarations
    function _event<T extends string | number>(
        eventType: T,
        target: EventTargetLike = Game.event,
        isPass: (...args: any) => boolean = () => true,
        cancellable: boolean = false,
        withArgs: boolean = false,
    ): Promise<T | [T, any]> | [Promise<T | [T, any]>, () => void] {
        let cancelCallback: () => void;
        const promise = new Promise<T | [T, any]>((resolveFunc) => {
            const eventCallback = (...arg: any) => {
                if (isPass(...arg)) {
                    resolveFunc(withArgs ? [eventType, arg] : eventType);
                } else {
                    temp();
                }
            };
            const temp = () => {
                target.once(eventType, eventCallback);
            };
            if (cancellable) {
                cancelCallback = () => {
                    target?.off(eventType, eventCallback);
                };
            }
            temp();
        });
        if (cancellable) {
            return [promise, cancelCallback];
        }
        return promise;
    }

    // const decorator = Decorator.methodCallCounter((args: Parameters<typeof _event>) => `Delay.event(${args[0].toString()})`);
    // export const event: typeof _event = decorator(Delay, 'event', { value: _event }).value;
    export const event = _event;
}

export default Delay;
